import { supabase } from '../../lib/supabase';
import { analyzeWithGPT4 } from '../../lib/openai/client';
import { parseAnalysisResponse } from '../../lib/openai/parser';
import { handleError } from '../../lib/error-handling';
import { measurePerformance } from '../../lib/performance';
import { parseRiskFactors, parseGrowthPotential } from '../analysis';
import type { Analysis } from '../../types/models';
import type { AnalysisInput } from './types';

export async function analyzeCandidateJob({
  candidateId,
  jobId
}: AnalysisInput): Promise<Analysis> {
  return measurePerformance(async () => {
    try {
      // Get candidate and job details
      const [candidateResult, jobResult] = await Promise.all([
        supabase.from('candidates').select('*').eq('id', candidateId).maybeSingle(),
        supabase.from('jobs').select('*').eq('id', jobId).maybeSingle()
      ]);

      if (candidateResult.error) throw new Error('Candidate not found');
      if (jobResult.error) throw new Error('Job not found');

      // Analyze using GPT-4
      const analysisResponse = await analyzeWithGPT4(
        candidateResult.data.cv_text,
        jobResult.data.jd_text
      );

      // Parse the response
      console.log('Raw AI response length:', analysisResponse.length);
      console.log('AI response preview:', analysisResponse.substring(0, 500) + '...');
      
      const analysis = parseAnalysisResponse(analysisResponse);
      
      // Debug: Log interview perspectives
      console.log('💾 Analysis interview_perspectives:', analysis.interview_perspectives);
      console.log('📊 Perspectives keys:', Object.keys(analysis.interview_perspectives || {}));
      
      // Validate technical skills after parsing
      if (analysis.technical_skills) {
        console.log(`Parsed ${analysis.technical_skills.length} technical skills:`, 
          analysis.technical_skills.map(ts => `${ts.skill} (${ts.proficiency})`)
        );
        
        // Check for common issues
        const invalidSkills = analysis.technical_skills.filter(ts => 
          !ts.skill || ts.skill.includes('Unknown') || ts.skill.includes('Parse Error')
        );
        
        if (invalidSkills.length > 0) {
          console.warn('Found invalid technical skills:', invalidSkills);
        }
      } else {
        console.warn('No technical skills parsed from analysis');
      }
      
      // Get user ID and organization ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user's organization ID
      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!member?.organization_id) {
        throw new Error('User must be part of an organization to create analyses');
      }
      
      // Transform analysis data to match database schema
      const dbAnalysis = {
        candidate_id: candidateId,
        job_id: jobId,
        user_id: user.id,
        organization_id: member.organization_id,
        fit_score: Math.round(analysis.fit_score), // Round to integer for database
        verdict: analysis.verdict,
        reasoning: analysis.reasoning,
        technical_skills: analysis.technical_skills, // Store as JSONB
        risk_factors: analysis.risk_factors, // Store as JSONB array of objects
        growth_potential: analysis.growth_potential, // Store as JSONB
        interview_plan: analysis.interview_plan,
        interview_perspectives: analysis.interview_perspectives || {}, // Store all perspectives as JSONB
        selected_perspective: 'manager', // Default perspective
        notes: analysis.notes || null,
        created_at: new Date().toISOString()
      };
      
      // Save to database
      const { data, error } = await supabase
        .from('analysis')
        .insert([dbAnalysis])
        .select()
        .maybeSingle();

      if (error) throw error;

      // Transform database data back to Analysis type
      const fullAnalysis: Analysis = {
        id: data.id,
        candidate_id: data.candidate_id,
        job_id: data.job_id,
        created_at: data.created_at,
        fit_score: data.fit_score,
        verdict: data.verdict,
        reasoning: data.reasoning,
        technical_skills: data.technical_skills || [],
        risk_factors: parseRiskFactors(data.risk_factors), // Use the same parsing logic as history
        growth_potential: parseGrowthPotential(data.growth_potential), // Use the same parsing logic as history
        interview_plan: data.interview_plan || '',
        interview_perspectives: data.interview_perspectives || {}, // Include the new field
        selected_perspective: data.selected_perspective || 'manager', // Include the new field
        cv_summary: '',
        job_requirements: '',
        questions_used: [],
        suggestions: [],
        status: 'completed' as const,
        notes: data.notes || undefined
      };
      
      return fullAnalysis;
    } catch (error) {
      throw handleError(error);
    }
  }, 'analyze_candidate_job');
}